import React from 'react';

import createProductFormItems from "@/components/Products/forms/createProductFormItems";
import selectProductForm from "@/components/Products/forms/selectProductForm";

import Tabs from "@/components/Tabs/Tabs";
import Form from "@/components/Form";
import ImageWithLoader from "@/components/UiElements/ImageWithLoader";
import Button from "@/components/UiElements/Button";
import ListLabelDescription from "@/components/UiElements/ListLabelDescription";
import DeleteAbsoluteButton from "@/components/UiElements/DeleteAbsoluteButton";

import createProductForBundleStates from "../utils/createProductForBundleStates";
import selectProductForBundleStates from "../utils/selectProductForBundleStates";

import useStateManagement from "@/hooks/stateManagement/useStateManagement";


interface AddBundleItemProps {
    productId: string | null | undefined;
    callBack?: () => void;
    onUpdate?: () => void;
    bundleId: string | null | undefined;
}

const AddBundleItem: React.FC<AddBundleItemProps> = ({ productId, callBack, onUpdate, bundleId = null }) => {

    const [selectedTab, setSelectedTab] = React.useState<number>(0);
    const [selectedProduct, setSelectedProduct] = React.useState<any | null>(null);
    const [productForm, setProductForm] = React.useState<any>({});
    const [inProgress, setInProgress] = React.useState<boolean>(false);

    const selectedProductRef = React.useRef<any>(null);



    // ------------------------------------------------
    // Handle form changes
    // ------------------------------------------------
    const handleSelectProduct = (data: any) => {
        setSelectedProduct(data?.product_id?.data ? {...data?.product_id?.data, quantity: data.quantity} : null);
    }

    const handleFillProductForm = (data: any) => {
        setProductForm({
            "2d_image_url": data["2d_image_url"] || null,
            "name": data["name"] || null,
            "quantity": data["quantity"] || null,
            "pricing_id": data["pricing_id"]?.value || null,
            "_validation": !!(data["2d_image_url"] && data["name"] && data["quantity"])
        });
    }

    // ------------------------------------------------
    // Call services to handle bundle item creation
    // ------------------------------------------------
    const handleCreateProduct = async () => {
        setInProgress(true);
        await handleCreateProductSteps({
            productForm,
            productId,
            bundleId
        });
        onUpdate?.();
        //setInProgress(false);
    };


    const handleOnAddBundleItem = async () => {
        setInProgress(true);
        await handleAssignProductSteps({
            selectedProduct,
            productId,
            bundleId
        });
        onUpdate?.();
        //setInProgress(false);
    }



    // ------------------------------------------------
    // Handle UI changes
    // ------------------------------------------------
    const handleChangeTab = (tabIndex: number) => {
        setSelectedTab(tabIndex);
    }
    const handleDeselectProduct = () => {
        setSelectedProduct(null);
        selectedProductRef.current?.reset("product_id");
    }



    // ------------------------------------------------
    // Create steps for handling bundle creation
    // ------------------------------------------------
    const {
        runSteps: handleCreateProductSteps,
        stateInProgress : createProductInProgress,
    } = useStateManagement(
        {...createProductForBundleStates}
    )

    const {
        runSteps: handleAssignProductSteps,
        stateInProgress : assignProductInProgress,
    } = useStateManagement(
        {...selectProductForBundleStates}
    )


    const loading = assignProductInProgress || createProductInProgress;

    return (
        <>
            <div className={"flex flex-col gap-xxl w-[650px]"}>
                <div className={"border-b border-b-gray-300"}>
                    <Tabs
                        size={"sm"}
                        tabs={[
                            {name: 'Select Product'},
                            {name: 'Create New Product'}
                        ]}
                        selectedTab={selectedTab}
                        onSelectedChange={handleChangeTab}
                    />
                </div>





                <div className={`${selectedTab === 0 ? "" : "hidden"} flex flex-col gap-md`}>
                    <div className={`grid grid-cols-12 gap-md`}>
                        <Form
                            items={selectProductForm}
                            onChange={handleSelectProduct}
                            ref={selectedProductRef}
                        />
                    </div>
                    {selectedProduct && (
                        <div className={"flex flex-col gap-sm "}>
                            <div className="select-none flex items-center justify-between gap-md text-gray-900 text-text-md col-span-12 break-line">Selected product details</div>
                            <div className={`py-xs rounded-md bg-gray-100 px-md w-full min-h-[165px] flex items-center gap-md relative shadow border border-gray-300`}>

                                <ImageWithLoader src={selectedProduct["2d_image_url"]} alt={selectedProduct.name} className="w-[120px] min-w-[120px] h-[120px] rounded-sm select-none"/>
                                <span className={"flex flex-col text-title-md py-md gap-md"}>
                                    <span className={"break-words"}>{selectedProduct.name}</span>
                                    <ListLabelDescription
                                        list={[
                                            {label: "SKU:", description: selectedProduct.sku},
                                            {label: "Variant ID:", description: selectedProduct.variant_id},
                                            {label: "Customizable:", description: selectedProduct.is_enable_customization ? "Yes" : "No"},
                                            {label: "Section" + (selectedProduct.sections.length > 1 ? "s:" : ":"), description: selectedProduct.sections.length > 0 ? selectedProduct.sections?.map((s: { section_name: string }) => s.section_name).join(", ") : "Empty"}
                                        ]}

                                        classNames={{
                                            descriptions: "!text-gray-600",
                                            labels: "text-gray-400 select-none"
                                        }}
                                    />
                                </span>
                                <DeleteAbsoluteButton onClick={handleDeselectProduct} />

                            </div>
                        </div>

                    )}

                </div>






                <div className={`${selectedTab === 1 ? "" : "hidden"} flex flex-col gap-xxl`}>
                    <div className={`grid grid-cols-12 gap-md `}>
                        <Form
                            items={createProductFormItems}
                            onChange={handleFillProductForm}
                        />
                    </div>

                </div>






                <div className={"flex flex-row gap-md justify-end pt-lg border-t border-gray-300"}>
                    <Button onClick={()=>callBack?.()} type={"alt"} disabled={loading || inProgress}>Cancel</Button>
                    {selectedTab === 0 && (
                        <Button
                            onClick={handleOnAddBundleItem}
                            disabled={!selectedProduct || loading || inProgress}
                        >
                            Add Selected Product
                        </Button>
                    )}
                    {selectedTab === 1 && (
                        <Button
                            onClick={handleCreateProduct}
                            disabled={!productForm["_validation"] || loading || inProgress}
                        >
                            Create And Add New Product
                        </Button>
                    )}
                </div>
            </div>
        </>
    );
};

export default AddBundleItem;
